import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useClientSocket } from './ClientSocketContext';
import { sessionFetch } from '../session-client';

interface ConversationUnread {
  conversationId: number;
  teamId: number | null;
  count: number;
}

interface ActivitySummary {
  bellUnread: number;
  chatTotal: number;
  conversations: ConversationUnread[];
}

interface ChatActivityContextValue {
  /** Bell badge: unviewed notifications not surfaced on the chat launcher */
  bellUnread: number;
  /** Chat launcher badge: unread chat mentions across all teams */
  chatTotal: number;
  /** Unread chat mentions per conversation id */
  unreadByConversation: Map<number, number>;
  /** Unread chat mentions summed for one team (dock is team-scoped) */
  teamUnread: (teamId: number) => number;
  /** Mark a conversation read (opened/focused); clears its unread mentions */
  markConversationRead: (conversationId: number) => void;
  /** True while the activity socket is connected (chat presence is live) */
  connected: boolean;
}

const ChatActivityContext = createContext<ChatActivityContextValue>({
  bellUnread: 0,
  chatTotal: 0,
  unreadByConversation: new Map(),
  teamUnread: () => 0,
  markConversationRead: () => {},
  connected: false,
});

export const useChatActivity = () => useContext(ChatActivityContext);

export const ChatActivityProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { subscribe, connected } = useClientSocket();
  const [summary, setSummary] = useState<ActivitySummary>({
    bellUnread: 0,
    chatTotal: 0,
    conversations: [],
  });

  // The activity channel of the shared client socket pushes the summary
  useEffect(() => {
    const unsubscribe = subscribe((frame) => {
      if (frame.ch === 'activity') {
        setSummary({
          bellUnread: frame.bellUnread ?? 0,
          chatTotal: frame.chatTotal ?? 0,
          conversations: frame.conversations ?? [],
        });
      }
    });
    return unsubscribe;
  }, [subscribe]);

  useEffect(() => {
    if (!connected) {
      setSummary({ bellUnread: 0, chatTotal: 0, conversations: [] });
    }
  }, [connected]);

  const markConversationRead = useCallback((conversationId: number) => {
    // Optimistically clear; the socket will confirm the recomputed totals
    setSummary((prev) => {
      const cleared = prev.conversations.find(
        (c) => c.conversationId === conversationId,
      );
      if (!cleared || cleared.count === 0) return prev;
      return {
        ...prev,
        chatTotal: Math.max(0, prev.chatTotal - cleared.count),
        conversations: prev.conversations.map((c) =>
          c.conversationId === conversationId ? { ...c, count: 0 } : c,
        ),
      };
    });
    void sessionFetch(`/api/chat-conversations/${conversationId}/read`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => undefined);
  }, []);

  const unreadByConversation = new Map<number, number>();
  for (const c of summary.conversations) {
    if (c.count > 0) unreadByConversation.set(c.conversationId, c.count);
  }

  const teamUnread = useCallback(
    (teamId: number) =>
      summary.conversations
        .filter((c) => c.teamId === teamId)
        .reduce((sum, c) => sum + c.count, 0),
    [summary.conversations],
  );

  return (
    <ChatActivityContext.Provider
      value={{
        bellUnread: summary.bellUnread,
        chatTotal: summary.chatTotal,
        unreadByConversation,
        teamUnread,
        markConversationRead,
        connected,
      }}>
      {children}
    </ChatActivityContext.Provider>
  );
};
